import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

type LazyVideoProps = {
  src: string;
  poster?: string;
  className?: string;
  ariaLabel?: string;
  showMuteToggle?: boolean;
};

/**
 * Defers the video download until it scrolls near the viewport, so the
 * initial page load only pays for the lightweight poster image.
 */
export function LazyVideo({
  src,
  poster,
  className = "w-full h-auto block",
  ariaLabel,
  showMuteToggle = false,
}: LazyVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || shouldLoad) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !shouldLoad) return;
    el.load();
    el.play().catch(() => {});
  }, [shouldLoad]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !isMuted;
    video.muted = next;
    setIsMuted(next);
    if (!next) {
      video.play().catch(() => {
        video.muted = true;
        setIsMuted(true);
      });
    }
  };

  return (
    <>
      <video
        ref={videoRef}
        {...(shouldLoad ? { src } : {})}
        poster={poster}
        autoPlay
        preload="none"
        muted
        loop
        playsInline
        className={className}
        aria-label={ariaLabel}
      />
      {showMuteToggle && (
        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? "Unmute video" : "Mute video"}
          className="absolute bottom-4 right-4 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-background/80 backdrop-blur-sm px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          <span className="hidden sm:inline">{isMuted ? "Sound off" : "Sound on"}</span>
        </button>
      )}
    </>
  );
}
